import { useState, type ReactNode } from "react";
import { ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Pagination, PaginationContent, PaginationItem } from "@/components/ui/pagination";
import { IntentButton } from "./intent-button";
import { EmptyState } from "./empty-state";
import { LoadingState } from "./loading-state";
import { cn } from "@/lib/utils";

export type SortDirection = "asc" | "desc";

export interface DataGridColumn<T> {
  id: string;
  header: ReactNode;
  accessor: (row: T) => ReactNode;
  sortable?: boolean;
  align?: "left" | "right" | "center";
  width?: string;
}

export interface DataGridProps<T> {
  rows: readonly T[];
  columns: readonly DataGridColumn<T>[];
  rowKey: (row: T) => string | number;
  totalRows: number;
  pageIndex: number;
  pageSize: number;
  loading?: boolean;
  emptyHeadline?: string;
  onPageChange: (next: number) => void;
  onPageSizeChange?: (size: number) => void;
  onSortChange?: (sort: { columnId: string; direction: SortDirection }) => void;
  onRowClick?: (row: T) => void;
}

export function DataGrid<T>({
  rows,
  columns,
  rowKey,
  totalRows,
  pageIndex,
  pageSize,
  loading,
  emptyHeadline = "No data",
  onPageChange,
  onPageSizeChange,
  onSortChange,
  onRowClick,
}: DataGridProps<T>) {
  // Sort state is uncontrolled. Parents drive server-side sort via onSortChange;
  // to reset the indicator (e.g. from a "Clear filters" action), remount this
  // component (e.g. change its `key`).
  const [sort, setSort] = useState<{ columnId: string; direction: SortDirection } | null>(null);
  const lastPage = Math.max(0, Math.ceil(totalRows / pageSize) - 1);

  const toggleSort = (columnId: string) => {
    const next: SortDirection =
      sort?.columnId === columnId && sort.direction === "asc" ? "desc" : "asc";
    const value = { columnId, direction: next };
    setSort(value);
    onSortChange?.(value);
  };

  // Loading state hides header + pagination via early return because the
  // server-driven sort/page are in-flight and an interactive header would be
  // misleading. The empty state (below) does NOT do this — it renders inside
  // the table body so the user can still try a different sort/filter.
  if (loading) return <LoadingState />;

  return (
    <div className="flex flex-col gap-2">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((col) => {
                const ariaSort: "none" | "ascending" | "descending" | undefined = col.sortable
                  ? sort?.columnId === col.id
                    ? sort.direction === "asc"
                      ? "ascending"
                      : "descending"
                    : "none"
                  : undefined;
                const headerLabel = typeof col.header === "string" ? col.header : col.id;
                return (
                  <TableHead
                    key={col.id}
                    aria-sort={ariaSort}
                    style={col.width ? { width: col.width } : undefined}
                    className={cn(col.align === "right" && "text-right")}
                  >
                    {col.sortable ? (
                      <button
                        type="button"
                        aria-label={`Sort by ${headerLabel}`}
                        className="inline-flex items-center gap-1"
                        onClick={() => toggleSort(col.id)}
                      >
                        {col.header}
                        {sort?.columnId === col.id ? (
                          sort.direction === "asc" ? (
                            <ChevronUp className="size-3" />
                          ) : (
                            <ChevronDown className="size-3" />
                          )
                        ) : null}
                      </button>
                    ) : (
                      col.header
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="p-0">
                  <EmptyState headline={emptyHeadline} />
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow 
                  key={rowKey(row)} 
                  className={cn("h-[32px]", onRowClick && "cursor-pointer hover:bg-slate-100")}
                  onClick={() => onRowClick?.(row)}
                >
                  {columns.map((col) => (
                    <TableCell key={col.id} className={cn(col.align === "right" && "text-right")}>
                      {col.accessor(row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {totalRows > 0 ? (
        <div className="flex items-center justify-between py-0.5">
          <div className="flex items-center gap-6 px-2">
            {onPageSizeChange && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground whitespace-nowrap">Rows per page:</span>
                <select
                  value={pageSize}
                  onChange={(e) => onPageSizeChange(Number(e.target.value))}
                  className="h-8 rounded-md border border-slate-300 bg-white px-2 py-1 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            )}
            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              Tracking {totalRows} {totalRows === 1 ? "item" : "items"}
            </div>
          </div>
          <Pagination className="justify-end w-auto mx-0 pr-2">
            <PaginationContent className="gap-1">
              <PaginationItem>
                <IntentButton 
                  intent="ghost" 
                  onClick={() => onPageChange(pageIndex - 1)} 
                  disabled={pageIndex === 0}
                  aria-label="Previous"
                  className="h-8 w-8 p-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                </IntentButton>
              </PaginationItem>
              {lastPage > 0 && (
                <PaginationItem>
                  <span className="px-3 text-sm text-muted-foreground">
                    Page {pageIndex + 1} of {lastPage + 1}
                  </span>
                </PaginationItem>
              )}
              <PaginationItem>
                <IntentButton
                  intent="ghost"
                  onClick={() => onPageChange(pageIndex + 1)}
                  disabled={pageIndex >= lastPage}
                  aria-label="Next"
                  className="h-8 w-8 p-0"
                >
                  <ChevronRight className="h-4 w-4" />
                </IntentButton>
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      ) : null}
    </div>
  );
}
